import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../../../utils/cn';

const STATUS_STYLES = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  OPEN: 'bg-blue-100 text-blue-700',
  PARTIALLY_FILLED: 'bg-blue-100 text-blue-700',
  FILLED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-gray-100 text-gray-500',
  REJECTED: 'bg-red-100 text-red-700',
};

const STATUS_LABELS = {
  PENDING: 'Chờ xử lý',
  OPEN: 'Đang mở',
  PARTIALLY_FILLED: 'Khớp một phần',
  FILLED: 'Đã khớp',
  CANCELLED: 'Đã hủy',
  REJECTED: 'Bị từ chối',
};

function formatVnd(value) {
  if (value == null) return '--';
  return value.toLocaleString('vi-VN') + ' VND';
}

function formatDate(dateStr) {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const PENDING_STATUSES = ['PENDING', 'OPEN'];

export default function OrderRow({ order, onClick, onOpenWebview, onSyncStatus }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const menuRef = useRef(null);
  const isBuy = order.side === 'BUY';
  const isPending = PENDING_STATUSES.includes(order.status);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleSync = async (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    if (!onSyncStatus) return;
    setSyncing(true);
    await onSyncStatus(order.id);
    setSyncing(false);
  };

  const handleWebview = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    onOpenWebview?.();
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-all hover:border-blue-200 hover:shadow-md">
      <button
        type="button"
        onClick={() => onClick(order)}
        className="w-full text-left"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-gray-900">{order.symbol || 'N/A'}</span>
            <span className={cn(
              'rounded-full px-2 py-0.5 text-xs font-semibold',
              isBuy ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            )}>
              {isBuy ? 'Mua' : 'Bán'}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={cn(
              'rounded-full px-2 py-0.5 text-xs font-medium',
              STATUS_STYLES[order.status] || 'bg-gray-100 text-gray-500'
            )}>
              {syncing ? 'Đang đồng bộ...' : (STATUS_LABELS[order.status] || order.status)}
            </span>
            <div ref={menuRef} className="relative">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMenuOpen((prev) => !prev);
                }}
                className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full z-20 mt-1 min-w-[180px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                  <button
                    type="button"
                    onClick={handleSync}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
                  >
                    <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Đồng bộ trạng thái
                  </button>
                  {onOpenWebview && (
                    <button
                      type="button"
                      onClick={handleWebview}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
                    >
                      <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                      </svg>
                      Quản lý trên Fmarket
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-2 flex items-end justify-between">
          <div>
            <p className="text-xs text-gray-500">Số tiền</p>
            <p className="text-sm font-semibold text-gray-900">{formatVnd(order.amount)}</p>
          </div>
          <p className="text-xs text-gray-400">{formatDate(order.submittedAt)}</p>
        </div>

        {order.originalStatus && (
          <p className="mt-1 text-xs text-gray-400">{order.originalStatus}</p>
        )}
      </button>
    </div>
  );
}
